import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ApiService from "services/ApiService";
import Loader from "components/Loader";
import moment from "moment";
import Constants from "utils/Constants";
import CricketRegistration from "./Templates/Cricket";
import TennisRegistration from "./Templates/Tennis";
import PokerRegistration from "./Templates/Poker";
import RunRegistration from "./Templates/Run";
import VolleybalRegistration from "./Templates/Volleyball";
import BadmintonRegistration from "./Templates/Badminton";
import EditEvent from "./EditEvent";
import Alert from "components/Alert";
import Button from "components/Button";
import useApiHandler from "hooks/useApiHandler";
import Turnstile, { CAPTCHA_ENABLED } from "components/Turnstile";

const EventRegistration = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);

  const eventApi = useApiHandler();
  const registerEventApi = useApiHandler();

  const [openEditEvent, setOpenEditEvent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [captchaError, setCaptchaError] = useState(null);

  const getEvent = useCallback(async () => {
    eventApi.request(
      {
        api: ApiService.getEventById,
        body: eventId,
        config: {
          displayError: false,
        },
      },
      (response, err) => {
        if (err) return;

        setEvent(response.data.event);
      },
    );
  }, [eventApi, eventId]);

  useEffect(() => {
    getEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerEvent = async (values) => {
    try {
      if (CAPTCHA_ENABLED && !captchaToken) {
        setCaptchaError("Please complete the verification");
        return;
      }
      setCaptchaError(null);
      let team = values["players"];
      delete values["players"];
      let registrationDetails = {
        ...values,
        team: team,
        event: event._id,
      };

      const body = {
        payment: {
          transactionType:
            values["registerThrough"] === "PAYPAL"
              ? Constants.EVENT_REGISTRATION_TRANSACTION_TYPE_PAYPAL
              : Constants.EVENT_REGISTRATION_TRANSACTION_TYPE_OFFLINE,
          amount: values.eventCost,
          intent: Constants.INTENT,
          currency: Constants.CURRENCY,
          paymentType: Constants.EVENT_REGISTRATION_PAYMENT_TYPE,
        },
        registration: registrationDetails,
        captchaToken: captchaToken,
      };

      registerEventApi.request(
        {
          api:
            values["registerThrough"] === "PAYPAL"
              ? ApiService.registerEventPaypal
              : ApiService.registerEventOffline,
          body: body,
          config: {
            displayError: true,
            continuousLoading: true,
          },
        },
        (response, err) => {
          setCaptchaReset((n) => n + 1);
          if (err) return;

          if (values["registerThrough"] === "PAYPAL") {
            localStorage.setItem(
              "registrationDetails",
              JSON.stringify(registrationDetails),
            );
            let link = response.data.url;
            window.open(link, "_self");
          } else {
            navigate("/event-registration-offline");
          }
        },
      );
    } catch (error) {
      console.log(error);
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const startDay = startDate.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
    const endDay = endDate.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
    const month = startDate.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  
    return `${month} ${startDay} – ${endDay}`;
  };

  if (eventApi.loading || registerEventApi.loading) {
    return <Loader open={eventApi.loading || registerEventApi.loading} />;
  }

  if (
    !eventApi.loading &&
    eventApi.error &&
    eventApi.event?.eventType?.value !== "badminton"
  ) {
    if (eventApi.errorCode === "hold" || eventApi.errorCode === "reg_closed") {
      return (
        <div className="mx-auto container md:py-20">
          <Alert type="warning"> {eventApi.message} </Alert>
        </div>
      );
    }

    return (
      <div className="mx-auto container md:py-20">
        <Alert type="danger"> {eventApi.message} </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto container md:py-20">
      <EditEvent
        open={openEditEvent}
        setOpen={setOpenEditEvent}
        refreshEvent={getEvent}
        event={event}
      />

      {event && (
        <>
          <div class="mx-auto bg-white  rounded-xl overflow-hidden p-6">
            <div class="flex flex-col md:flex-row gap-6">
              {event.flyer && (
                <div class="md:w-1/2">
                  <img
                    src={ApiService.getMedia(event?.flyer)}
                    alt="Event Flyer"
                    class="w-full h-auto rounded-md shadow"
                  />
                </div>
              )}

              <div class="md:w-1/2 space-y-4">
                <h1 class="text-3xl font-bold text-gray-800">
                  {event.eventName}
                </h1>

                <p class="text-gray-700">{event.eventDescription}</p>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700 mb-6">
                  {event?.eventVenue && (
                    <div class="bg-gray-50 p-4 rounded">
                      <strong class="block text-gray-900 mb-1">Venue:</strong>
                      <div dangerouslySetInnerHTML={{__html: event?.eventVenue}}></div>
                    </div>
                  )}
                  {event?.geoLocation && (
                    <div class="bg-gray-50 p-4 rounded">
                      <strong class="block text-gray-900 mb-1">
                        Geo Location:
                      </strong>
                      {event?.geoLocation}
                    </div>
                  )}
                  <div class="bg-gray-50 p-4 rounded">
                    <strong class="block text-gray-900 mb-1">
                      Registration Fee:
                    </strong>
                   <RenderCost event={event} />
                  </div>
                  {!event.isHold && (
                    <div class="bg-gray-50 p-4 rounded">
                      <strong class="block text-gray-900 mb-1">
                        Dates:
                      </strong>
                      {/* Aug 1 – 3 & Aug 8 – 10 */}
                      {/* {`${formatDateRange(event?.week1StartDate, event?.week1EndDate)} & ${formatDateRange(event?.week2StartDate, event?.week2EndDate)}`} */}
                      {/* {`${formatDateRange(event?.eventStartDate, event?.eventEndDate)}`} */}
                      {moment(event?.eventStartDate).add(1, 'day').format("MMM D")} - {moment(event?.eventEndDate).add(1, 'day').format("MMM D")}
                    </div>
                  )}
                </div>

                {!event.isHold &&
                  !event.isPrevious &&
                  !event.isDisabled &&
                  auth.isLoggedIn &&
                  auth.isAdmin && (
                    <div className="mt-4">
                      <Button onClick={() => setOpenEditEvent(true)}>
                        Update Event
                      </Button>
                    </div>
                  )}
              </div>
            </div>
          </div>

          {event?.eventType?.value === "badminton" && event.isHold && (
            <div
              class=" mt-4 flex items-center p-4 mb-4 text-sm text-yellow-800 border border-yellow-300 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300 dark:border-yellow-800"
              role="alert"
            >
              <div>
                {event.holdReason ||
                  `Thank you for your overwhelming support! We have closed
                registrations and wish everyone good luck for the Dhvani
                Pickleball Tournament.`}
              </div>
            </div>
          )}

          {event?.eventType?.value === "tennis" && event.isHold && (
            <div
              class=" mt-4 flex items-center p-4 mb-4 text-sm text-yellow-800 border border-yellow-300 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300 dark:border-yellow-800"
              role="alert"
            >
              <div>
                {event.holdReason ||
                  `Thank you for your overwhelming support! We have closed
                registrations and wish everyone good luck for the Dhvani Tennis
                Tournament.`}
              </div>
            </div>
          )}

          {!event.isHold && !event.isPrevious && !event.isDisabled && (
            <div className="my-10 px-5 lg:px-0">
              <h2 className="mb-2 text-gray-500 font-medium text-lg">
                {" "}
                {event.eventName}{" "}
              </h2>

              {/* {["badminton"].includes(event?.eventType?.value) && (
                <div
                  class="flex items-center p-4 mb-4 text-sm text-yellow-800 border border-yellow-300 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300 dark:border-yellow-800"
                  role="alert"
                >
                  <div>
                    <span class="font-medium">Note: </span> All start times are
                    subject to change, and events may be cancelled based on
                    registration numbers. If we cancel a particular category, we
                    will notify the affected players directly during the week of
                    February 12th.
                    <br />
                    <br />
                    The detailed tournament format and rules will be provided to
                    all registered players soon. Stay tuned for further
                    communication and check back here regularly for updates.
                  </div>
                </div>
              )} */}

              {/* {["cricket"].includes(event?.eventType?.value) && (
                <div class="flex items-center p-4 mb-4 text-sm text-yellow-800 border border-yellow-300 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300 dark:border-yellow-800" role="alert">
                  <div>
                    <span class="font-medium">Note: </span> Register your team by August 10th to secure guaranteed team t-shirts. Design and color choices are available on a first-come, first-served basis. Take advantage of this opportunity now!
                    <br />
                    <br />
                    With overwhelming support, we have reached our maximum Men's and Kids under 17 team's count for now!  Registrations are closed for the moment. If there are any changes, we will let you know. Thank you for your understanding!
                  </div>
                </div>
              )} */}

              <div className="text-2xl text-gray-500 font-medium mb-2">
                REGISTER HERE:
              </div>

              <div className="mb-4">
                <Turnstile
                  action="event-registration"
                  onVerify={setCaptchaToken}
                  resetKey={captchaReset}
                  error={captchaError}
                />
              </div>

              {event.eventType?.value === "cricket" && (
                <CricketRegistration
                  registerEvent={registerEvent}
                  event={event}
                />
              )}
              {event.eventType?.value === "tennis" && (
                <TennisRegistration
                  registerEvent={registerEvent}
                  event={event}
                  eventType="tennis"
                />
              )}
              {event.eventType?.value === "poker" && (
                <PokerRegistration
                  registerEvent={registerEvent}
                  event={event}
                />
              )}
              {event.eventType?.value === "run" && (
                <RunRegistration registerEvent={registerEvent} event={event} />
              )}
              {event.eventType?.value === "volleyball" && (
                <VolleybalRegistration
                  registerEvent={registerEvent}
                  event={event}
                />
              )}
              {event.eventType?.value === "badminton" && (
                <BadmintonRegistration
                  registerEvent={registerEvent}
                  event={event}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RenderCost = ({ event }) => {
  const [costs, setCosts] = useState([]);

  useEffect(() => {
    if (event.eventCost) {
      let keys = Object.keys(event.eventCost);
      let values = Object.values(event.eventCost);

      let eventCosts = [];
      for (let i = 0; i < keys.length; i++) {
        let keyNameArr = keys[i].split("-");
        let keyName = keyNameArr.join(" ");
        keyName = keyName.charAt(0).toUpperCase() + keyName.slice(1);
        eventCosts.push({
          name: keyName,
          cost: values[i],
        });
      }

      setCosts(eventCosts);
    }
  }, [event]);

  if (!event.eventCost["default"]) {
    return (
      <div>
        {costs.map((ec, idx) => (
          <div key={idx}>
            {" "}
            {ec.name} - ${ec.cost}{" "}
          </div>
        ))}
      </div>
    );
  } else {
    return <span> ${event.eventCost["default"]} </span>;
  }
};

export default EventRegistration;
